// The liquid surface shader (docs/28 L3): one board-sized quad rendered to a texture at art
// resolution (one fragment per art pixel, like the weather's ground shader, docs/20), drawn in the
// light layer under the weather and the light map, so night darkens the water, a torch lights it
// and fog lies over it. Everything outside a liquid square is transparent.
//
// Per liquid (the mask's R channel), all of it slow and small:
//   water   sparkles where two sets of wavelets crest together, drifting with the wind; a foam line
//           lapping in and out along the bank; rain rings, melting-snow dimples, a cave's drip rings
//   deep    the same, plus a slow darker swell and, in a gale, whitecaps
//   lava    bright veins crawling through the crust, a slow pulse, bubbles that swell and pop
//   mud     a slow oily sheen, fat bubbles that swell, catch the light and pop, duller rain rings
//   frozen  water and mud as ice: a pale sheet, a crack or two per square, glints (field.rs: frozen
//           water is `Ice`, which can be walked on, so it must not look like water)
// Effects stay off the outer ~3 px of a square where it meets the bank: the Oryx shore art lives there.
//
// Mask (one texel per cell, NEAREST): R = liquid code × 50 (1 water, 2 deep, 3 lava, 4 mud), G = a
// prop stands on the square (lily pad: effects are damped), B = a per-cell roll. Output is
// premultiplied (Phaser's NORMAL blend): every step is "darken by d" or "lerp toward colour X by t".
export const LIQUID_FRAG = `
#pragma phaserTemplate(shaderName)
#pragma phaserTemplate(extensions)
#pragma phaserTemplate(features)
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
#pragma phaserTemplate(fragmentDefine)
varying vec2 outTexCoord;
#pragma phaserTemplate(outVariables)
#pragma phaserTemplate(fragmentHeader)
uniform sampler2D uMask;
uniform vec2 uSize;        // board size in art pixels
uniform vec2 uCells;       // board size in cells
uniform float uTime;       // seconds (frozen under reduce motion)
uniform float uGlint;      // sparkle cap (GLINT_CAP)
uniform float uLap;        // foam line
uniform float uSwell;      // the deep's darkening (SWELL_CAP)
uniform float uChop;       // whitecaps 0..1
uniform vec2 uFlow;        // surface drift, art px per second (board axes, y down)
uniform float uRings;      // rain rings 0..1
uniform float uRingSize;   // how far a rain ring spreads (art px)
uniform float uDimples;    // snow melting on the water 0..1
uniform float uDrips;      // cave drips 0..1
uniform float uLava;       // vein brightness (LAVA_CAP)
uniform float uLavaBubbles;
uniform float uSheen;      // mud sheen
uniform float uMudBubbles;
uniform float uIce;        // 1 frozen
uniform vec3 uSky;

float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 3; i++) { v += a * noise(p); p = p * 2.07 + vec2(17.1, 9.2); a *= 0.5; }
  return v;
}

vec4 darken(vec4 acc, float d) { return vec4(acc.rgb * (1.0 - d), 1.0 - (1.0 - acc.a) * (1.0 - d)); }
vec4 toward(vec4 acc, vec3 x, float t) { return vec4(acc.rgb * (1.0 - t) + x * t, 1.0 - (1.0 - acc.a) * (1.0 - t)); }

float codeAt(vec2 tc) { return floor(texture2D(uMask, tc).r * 255.0 / 50.0 + 0.5); }
// water and deep are one body of water (the tiler draws no shore between them)
float bodyOf(float c) { return (c > 1.5 && c < 2.5) ? 1.0 : c; }

// A ring slot: the board is cut into g×g px slots, each with its own clock. Returns
// (age 0..1, live 0/1, distance from the slot's centre in a flattened ellipse, slot hash).
vec4 slot(vec2 px, float g, float period, float spread, float share, float salt) {
  vec2 id = floor(px / g);
  float h = hash(id + salt);
  float p = period * (0.7 + 0.6 * h);
  float clock = uTime / p + h * 7.0;
  float age = fract(clock);
  float live = step(hash(id + floor(clock) * 1.37 + salt * 2.1), share);
  vec2 c = id * g + g * 0.5 + (vec2(hash(id + 3.7 + salt), hash(id + 9.1 + salt)) - 0.5) * spread;
  vec2 d = (px - c) * vec2(1.0, 1.6);
  return vec4(age, live, length(d), h);
}

void main() {
  vec2 tc = (floor(outTexCoord * uCells) + 0.5) / uCells;   // this square's centre (mask space)
  float c0 = codeAt(tc);
  if (c0 < 0.5) { gl_FragColor = vec4(0.0); return; }
  vec4 m = texture2D(uMask, tc);
  vec2 uv = vec2(outTexCoord.x, 1.0 - outTexCoord.y);       // top-left origin, like the board
  vec2 px = floor(uv * uSize) + 0.5;                          // the art pixel
  vec2 cell = px / uSize * uCells;
  float cpx = uSize.x / uCells.x;                             // art px per square
  float t = uTime;
  float b0 = bodyOf(c0);
  float damp = 1.0 - 0.7 * m.g;
  float roll = m.b;

  // distance (art px) to the nearest square of another body: straight banks from the cardinals,
  // rounded corners from the diagonals (mask space throughout, so the flip does not matter)
  vec2 f = fract(outTexCoord * uCells);
  vec2 cs = 1.0 / uCells;
  float land = 1.0e3;
  if (abs(bodyOf(codeAt(tc + vec2(cs.x, 0.0))) - b0) > 0.5) land = min(land, 1.0 - f.x);
  if (abs(bodyOf(codeAt(tc - vec2(cs.x, 0.0))) - b0) > 0.5) land = min(land, f.x);
  if (abs(bodyOf(codeAt(tc + vec2(0.0, cs.y))) - b0) > 0.5) land = min(land, 1.0 - f.y);
  if (abs(bodyOf(codeAt(tc - vec2(0.0, cs.y))) - b0) > 0.5) land = min(land, f.y);
  if (abs(bodyOf(codeAt(tc + cs)) - b0) > 0.5) land = min(land, length(vec2(1.0) - f));
  if (abs(bodyOf(codeAt(tc - cs)) - b0) > 0.5) land = min(land, length(f));
  if (abs(bodyOf(codeAt(tc + vec2(cs.x, -cs.y))) - b0) > 0.5) land = min(land, length(vec2(1.0 - f.x, f.y)));
  if (abs(bodyOf(codeAt(tc + vec2(-cs.x, cs.y))) - b0) > 0.5) land = min(land, length(vec2(f.x, 1.0 - f.y)));
  float edge = land * cpx;
  float inner = smoothstep(2.5, 4.5, edge);     // past the shore art
  vec4 acc = vec4(0.0);

  // ---- ice: frozen water and mud ----
  if (uIce > 0.0 && b0 != 3.0) {
    float sheet = smoothstep(1.5, 3.5, edge);
    vec3 iceC = b0 == 4.0 ? vec3(0.72, 0.76, 0.8) : vec3(0.8, 0.89, 0.97);
    acc = toward(acc, iceC, 0.55 * uIce * sheet * (0.9 + 0.1 * noise(cell * 1.3)));
    // a crack or two per square: a jagged line through a point near the middle
    vec2 sq = floor(cell);
    vec2 o = (sq + 0.5) * cpx + (vec2(hash(sq + 1.1), hash(sq + 2.3)) - 0.5) * cpx * 0.3;
    float a = hash(sq + 4.7) * 3.1416;
    vec2 dir = vec2(cos(a), sin(a));
    vec2 rel = px - o;
    float along = dot(rel, dir);
    float across = dot(rel, vec2(-dir.y, dir.x)) + (hash(sq + floor(along / 2.0)) - 0.5) * 1.4;
    float crack = step(abs(across), 0.5) * step(abs(along), cpx * 0.38) * step(0.3, hash(sq + 6.2));
    vec2 dir2 = vec2(cos(a + 1.1), sin(a + 1.1));
    float along2 = dot(rel, dir2);
    float across2 = dot(rel, vec2(-dir2.y, dir2.x)) + (hash(sq + floor(along2 / 2.0) + 8.0) - 0.5) * 1.4;
    crack = max(crack, step(abs(across2), 0.5) * step(0.0, along2) * step(along2, cpx * 0.22) * step(0.55, hash(sq + 7.9)));
    acc = toward(acc, b0 == 4.0 ? vec3(0.42, 0.4, 0.38) : vec3(0.5, 0.64, 0.82), 0.6 * crack * uIce * sheet);
    // a long faint streak of light across the sheet, and a few glints that come and go
    float streak = step(0.985, sin((px.x - px.y * 1.3) * 0.21 + roll * 6.283)) * 0.22;
    acc = toward(acc, vec3(1.0), streak * uIce * sheet);
    float tw = step(0.993, hash(floor(px) + 2.9)) * (0.5 + 0.5 * sin(t * 2.7 + hash(floor(px)) * 40.0));
    acc = toward(acc, vec3(1.0), 0.85 * tw * uIce * sheet);
    gl_FragColor = acc;
    return;
  }

  // ---- water and the deep ----
  if (b0 == 1.0) {
    vec2 q = px - uFlow * t;                                   // the surface drifts with the wind
    if (c0 == 2.0) {
      // the deep: a slow darker swell rolling through
      float sw = 0.5 + 0.5 * sin(dot(cell, vec2(0.55, 0.8)) - t * 0.6 + noise(cell * 0.4 + 2.0) * 3.0);
      acc = darken(acc, uSwell * sw * inner);
    }
    // wavelets: two sets of crests crossing; a pixel sparkles where both crest, and only in
    // drifting patches, so the water twinkles here and there rather than all over
    float n1 = noise(q * 0.06 + vec2(t * 0.05, 0.0));
    float sp = 1.0 + 0.8 * uChop;
    float w1 = sin(q.x * 0.42 + q.y * 0.19 + t * 1.6 * sp + n1 * 5.0);
    float w2 = sin(-q.x * 0.23 + q.y * 0.47 - t * 1.25 * sp + n1 * 4.0);
    float patchy = smoothstep(0.4, 0.7, noise(q * 0.035 + 3.0 + vec2(0.0, t * 0.03)));
    float spark = step(1.84, w1 + w2) * patchy;
    float line = step(0.97, w1) * step(w2, -0.2) * patchy;     // a faint lit crest now and then
    vec3 glintC = mix(uSky, vec3(1.0), 0.65);
    float gk = uGlint * inner * damp * (c0 == 2.0 ? 0.75 : 1.0);
    acc = toward(acc, uSky, gk * 0.22 * line);
    acc = toward(acc, glintC, gk * spark);

    // whitecaps on a windy day: short white dashes that rise and break
    if (uChop > 0.0) {
      vec2 g = vec2(9.0, 7.0);
      vec2 id = floor(q / g);
      float h = hash(id + 13.0);
      float clock = t / (1.4 + 1.6 * h) + h * 5.0;
      float age = fract(clock);
      float live = step(hash(id + floor(clock) * 2.3), uChop * (c0 == 2.0 ? 0.5 : 0.22));
      vec2 local = q - id * g;
      vec2 c = vec2(2.0 + h * 4.0, 2.0 + hash(id + 1.0) * 3.0);
      float len = 1.6 * sin(age * 3.1416);
      float dash = step(abs(local.y - c.y), 0.5) * step(abs(local.x - c.x), len);
      acc = toward(acc, vec3(0.94, 0.97, 1.0), 0.65 * dash * live * inner * damp);
    }

    // the bank: a line of foam lapping in and out (just past the shore art) with a fainter one behind
    float lapPos = 3.0 + 1.4 * (0.5 + 0.5 * sin(t * 1.15 + roll * 6.283 + (cell.x + cell.y) * 0.7));
    float broken = step(0.38, noise(px * 0.28 + vec2(t * 0.25, 0.0)));
    float foam = step(lapPos, edge) * step(edge, lapPos + 1.0) * broken;
    float foam2 = step(lapPos + 2.0, edge) * step(edge, lapPos + 3.0) * step(0.62, noise(px * 0.31 + 5.0 - vec2(t * 0.2, 0.0)));
    vec3 foamC = mix(vec3(0.92, 0.96, 1.0), uSky, 0.2);
    acc = toward(acc, foamC, uLap * damp * (foam + 0.4 * foam2));
  }

  // ---- rain rings, melting snow and cave drips: on water and (duller) on mud ----
  if (b0 == 1.0 || b0 == 4.0) {
    vec3 ringC = b0 == 4.0 ? vec3(0.62, 0.55, 0.44) : mix(uSky, vec3(1.0), 0.5);
    float ringK = b0 == 4.0 ? 0.35 : 0.55;
    if (uRings > 0.0) {
      // a drop lands (a bright pixel), then its ring spreads and fades; two offset sets of slots
      vec4 s = slot(px, 13.0, 0.8, 2.0, 0.5 * uRings, 0.0);
      float r = 1.2 + s.x * uRingSize;
      float hit = step(s.z, 0.6) * step(s.x, 0.15);
      float ring = step(abs(s.z - r), 0.5) * step(0.12, s.x);
      acc = toward(acc, ringC, ringK * ((1.0 - s.x) * ring + hit) * s.y * inner * damp);
      vec4 s2 = slot(px + 6.5, 13.0, 0.95, 2.0, 0.32 * uRings, 31.0);
      float r2 = 1.2 + s2.x * uRingSize * 0.8;
      acc = toward(acc, ringC, ringK * (1.0 - s2.x) * step(abs(s2.z - r2), 0.5) * step(0.12, s2.x) * s2.y * inner * damp);
    }
    if (uDimples > 0.0) {
      // a flake lands: a bright pixel, then a one-pixel ring as it melts away
      vec4 s = slot(px, 7.0, 1.6, 3.0, 0.3 * uDimples, 57.0);
      float dot1 = step(s.z, 0.6) * step(s.x, 0.35);
      float ring = step(abs(s.z - 1.2), 0.45) * step(0.35, s.x) * step(s.x, 0.8);
      acc = toward(acc, vec3(0.95, 0.97, 1.0), 0.5 * (dot1 + 0.6 * ring) * (1.0 - s.x) * s.y * inner * damp);
    }
    if (uDrips > 0.0) {
      // a drop from the cave roof: a wide slow ring and a second following it
      vec4 s = slot(px, cpx, 3.6, cpx * 0.4, 0.22 * uDrips, 77.0);
      float r = 0.5 + s.x * 6.5;
      float ring = step(abs(s.z - r), 0.5) + 0.6 * step(abs(s.z - (r - 2.5)), 0.5) * step(0.3, s.x);
      acc = toward(acc, ringC, 0.5 * (1.0 - s.x) * ring * s.y * inner * damp);
    }
  }

  // ---- lava ----
  if (b0 == 3.0) {
    float hot0 = smoothstep(1.0, 3.0, edge);
    vec2 lq = cell * 1.05 - vec2(t * 0.045, t * 0.02);
    float n = fbm(lq + fbm(lq * 0.8 + vec2(t * 0.04, -t * 0.03)) * 0.9);
    float vein = 1.0 - abs(n * 2.0 - 1.0);                    // ridges where the noise crosses its middle
    float pulse = 0.82 + 0.18 * sin(t * 1.3 + roll * 6.283);
    acc = darken(acc, 0.14 * smoothstep(0.35, 0.1, vein) * hot0);   // the cooler crust
    acc = toward(acc, vec3(1.0, 0.6, 0.2), uLava * 0.45 * smoothstep(0.72, 0.9, vein) * pulse * hot0);
    acc = toward(acc, vec3(1.0, 0.9, 0.5), uLava * smoothstep(0.9, 0.98, vein) * pulse * hot0);
    // bubbles: swell bright, then pop into a ring
    vec4 s = slot(px, 12.0, 2.6, 4.0, 0.45 * uLavaBubbles, 101.0);
    float a = s.x;
    if (a < 0.75) {
      float r = 0.5 + 2.2 * a / 0.75;
      float disc = step(s.z, r);
      acc = toward(acc, vec3(1.0, 0.82, 0.38), 0.6 * disc * (a / 0.75) * s.y * inner);
      acc = toward(acc, vec3(0.5, 0.12, 0.04), 0.45 * step(abs(s.z - r - 0.5), 0.5) * s.y * inner);
    } else {
      float p = (a - 0.75) / 0.25;
      acc = toward(acc, vec3(1.0, 0.7, 0.3), 0.6 * (1.0 - p) * step(abs(s.z - (2.8 + 2.4 * p)), 0.5) * s.y * inner);
    }
  }

  // ---- bog mud ----
  if (b0 == 4.0) {
    vec2 mq = cell * 0.7 + vec2(t * 0.02, t * 0.012);
    float sn = noise(mq) * 0.7 + noise(mq * 2.3 + 4.0) * 0.3;
    // thin broken glints of sheen in drifting patches, not whole contour loops
    float sheen = step(0.6, sn) * step(sn, 0.63) * smoothstep(0.55, 0.75, noise(cell * 0.45 - vec2(t * 0.015, 0.0) + 9.0)) * step(0.45, hash(floor(px / 2.0)));
    acc = toward(acc, uSky, uSheen * sheen * inner * damp);
    // fat slow bubbles: a dark dome with a lit pixel, then a plop ring
    vec4 s = slot(px, 12.0, 4.5, 4.0, 0.3 * uMudBubbles, 151.0);
    float a = s.x;
    if (a < 0.8) {
      float r = 0.5 + 1.8 * a / 0.8;
      vec2 id = floor(px / 12.0);
      vec2 c = id * 12.0 + 6.0 + (vec2(hash(id + 154.7), hash(id + 160.1)) - 0.5) * 4.0;
      acc = toward(acc, vec3(0.2, 0.15, 0.09), 0.55 * step(s.z, r) * s.y * inner * damp);
      acc = toward(acc, vec3(0.62, 0.52, 0.36), 0.35 * step(abs(s.z - r - 0.5), 0.5) * s.y * inner * damp);
      vec2 hl = floor(c - vec2(r * 0.5, r * 0.6)) + 0.5;
      acc = toward(acc, mix(uSky, vec3(1.0), 0.4), 0.55 * step(length(px - hl), 0.5) * step(1.2, r) * s.y * inner * damp);
    } else {
      float p = (a - 0.8) / 0.2;
      acc = toward(acc, vec3(0.66, 0.56, 0.4), 0.55 * (1.0 - p) * step(abs(s.z - (2.0 + 2.6 * p)), 0.5) * s.y * inner * damp);
    }
  }

  gl_FragColor = acc;
}
`;
